<template>
    <BModal
        :visible="visible"
        size="xl"
        modal-class="review-workspace-modal"
        scrollable
        no-close-on-backdrop
        :hide-header-close="saving || completing"
        @hide="$emit('close')"
    >
        <template #modal-title>
            {{ translate('Component(ReviewProcess):reviewable-modal-label', 'Review document') }}
        </template>

        <BOverlay
            :show="loading"
            variant="white"
            spinner-variant="primary"
            blur="0"
            opacity=".85"
            rounded="sm"
        >
            <BAlert
                v-if="error"
                variant="danger"
                show
            >
                <div class="alert-body">
                    {{ error }}
                </div>
            </BAlert>

            <component
                :is="rendererComponent"
                v-if="rendererComponent && item && draft"
                :key="item.id"
                :renderer-options="rendererOptions"
                :value="draft"
                :item="item"
                :suggestions="suggestions"
                :review-context="reviewContext"
                :violations="violations"
                :workspace-service="workspaceService"
                @input="$emit('input', $event)"
                @initialization-change="$emit('renderer-initialization-change', $event)"
                @completion-validity-change="$emit('renderer-completion-validity-change', $event)"
                @completion-issues-change="$emit('renderer-completion-issues-change', $event)"
            />

            <div
                v-else-if="navigation && navigation.complete"
                class="review-workspace-complete-state text-center py-3 px-2"
            >
                <BBadge variant="success" class="mb-1">
                    {{ navigation.reviewed }} / {{ navigation.total }}
                </BBadge>
                <h5 class="mb-1">
                    {{ translate('Component(ReviewProcess):all-documents-reviewed', 'All documents reviewed') }}
                </h5>
                <p class="text-less-muted mb-0">
                    {{ translate(
                        'Component(ReviewProcess):all-documents-reviewed-description',
                        'You can return to any document or complete the review process.',
                    ) }}
                </p>
            </div>
        </BOverlay>

        <template #modal-footer>
            <div class="review-workspace-footer">
                <div
                    v-if="navigation"
                    class="review-workspace-footer__navigation"
                >
                    <BButton
                        v-if="resolvedNavigationOptions.showAdjacentNavigation"
                        variant="flat-secondary"
                        size="sm"
                        class="btn-icon"
                        :disabled="navigationDisabled || loading || saving || completing || !navigation.canGoPrevious"
                        @click="$emit('navigate-previous')"
                    >
                        <FeatherIcon icon="ChevronLeftIcon" size="18" />
                        <span class="sr-only">
                            {{ translate('Component(Button):previous', 'Previous') }}
                        </span>
                    </BButton>
                    <BFormSelect
                        v-if="resolvedNavigationOptions.showItemSelector"
                        :value="navigation.activeItemId"
                        :disabled="navigationDisabled || loading || saving || completing"
                        class="review-workspace-footer__select"
                        @input="$emit('navigate', $event)"
                    >
                        <option
                            :disabled="!navigation.summaryAvailable"
                            :value="navigation.summaryItemId"
                        >
                            {{ translate('Component(ReviewProcess):review-summary', 'Review summary') }}
                        </option>
                        <option
                            v-for="option in navigation.items"
                            :key="option.id"
                            :value="option.id"
                        >
                            {{ option.status === 'reviewed' ? '✓ ' : '' }}{{ option.position }} / {{ navigation.total }} · {{ option.label }}
                        </option>
                    </BFormSelect>
                    <BButton
                        v-if="resolvedNavigationOptions.showAdjacentNavigation"
                        variant="flat-secondary"
                        size="sm"
                        class="btn-icon"
                        :disabled="navigationDisabled || loading || saving || completing || !navigation.canGoNext"
                        @click="$emit('navigate-next')"
                    >
                        <FeatherIcon icon="ChevronRightIcon" size="18" />
                        <span class="sr-only">
                            {{ translate('Component(Button):next', 'Next') }}
                        </span>
                    </BButton>
                    <BBadge
                        v-if="resolvedNavigationOptions.showProgress"
                        variant="light-success"
                        class="review-workspace-footer__progress"
                    >
                        {{ navigationProgressLabel }}
                    </BBadge>
                </div>

                <div class="review-workspace-footer__actions">
                    <BButton
                        variant="outline-secondary"
                        :disabled="saving || completing"
                        @click="$emit('close')"
                    >
                        {{ translate('Component(Button):cancel', 'Cancel') }}
                    </BButton>
                    <BButton
                        v-if="showSaveAction"
                        variant="primary"
                        :disabled="loading || saving || completing || !saveActionAllowed"
                        @click="$emit('save')"
                    >
                        <BSpinner v-if="saving" small class="mr-50" />
                        {{ saveAction === 'save-and-next'
                            ? translate('Component(Button):save-and-next', 'Save and next')
                            : saveAction === 'save-and-complete'
                                ? translate('Component(Button):save-and-complete', 'Save and complete')
                            : translate('Component(Button):save-review', 'Save review') }}
                    </BButton>
                    <BButton
                        v-if="showDecisionActions && showRejectAction"
                        variant="danger"
                        :disabled="loading || saving || completing"
                        @click="$emit('complete', 'reject')"
                    >
                        <BSpinner
                            v-if="completing && completionDecision === 'reject'"
                            small
                            class="mr-50"
                        />
                        {{ translate('Component(Button):reject', 'Reject') }}
                    </BButton>
                    <span
                        v-if="showDecisionActions && showCompleteAction"
                        class="review-workspace-footer__complete-action"
                        :class="{
                            'review-workspace-footer__complete-action--blocked': !completionAllowed,
                        }"
                    >
                        <BButton
                            variant="primary"
                            :disabled="loading || saving || completing || !completionAllowed"
                            @click="$emit('complete', 'continue')"
                        >
                            <BSpinner
                                v-if="completing && completionDecision === 'continue'"
                                small
                                class="mr-50"
                            />
                            {{ completeActionLabel }}
                        </BButton>
                        <ReviewNeedsAttentionBadge
                            v-if="!completionAllowed && completionAttentionChecks.length"
                            compact
                            icon="InfoIcon"
                            :checks="completionAttentionChecks"
                            class="review-workspace-footer__complete-attention"
                        />
                    </span>
                </div>
            </div>
        </template>
    </BModal>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BFormSelect,
    BModal,
    BOverlay,
    BSpinner,
} from 'bootstrap-vue'
import ReviewNeedsAttentionBadge from '../document-compliance/components/ReviewNeedsAttentionBadge.vue'

export default {
    name: 'ModalReviewPresentation',
    components: {
        BAlert,
        BBadge,
        BButton,
        BFormSelect,
        BModal,
        BOverlay,
        BSpinner,
        ReviewNeedsAttentionBadge,
    },
    props: {
        visible: { type: Boolean, default: false },
        rendererComponent: { type: [Object, Function], default: null },
        rendererOptions: { type: Object, default: () => ({}) },
        item: { type: Object, default: null },
        draft: { type: Object, default: null },
        suggestions: { type: Array, default: () => [] },
        reviewContext: { type: Object, default: () => ({}) },
        violations: { type: Array, default: () => [] },
        workspaceService: { type: Object, required: true },
        loading: { type: Boolean, default: false },
        saving: { type: Boolean, default: false },
        completing: { type: Boolean, default: false },
        completionDecision: { type: String, default: null },
        error: { type: String, default: '' },
        showSaveAction: { type: Boolean, default: false },
        saveAction: { type: String, default: 'save' },
        saveActionAllowed: { type: Boolean, default: true },
        showDecisionActions: { type: Boolean, default: false },
        showRejectAction: { type: Boolean, default: true },
        showCompleteAction: { type: Boolean, default: true },
        completionAllowed: { type: Boolean, default: true },
        completionIssues: { type: Array, default: () => [] },
        navigation: { type: Object, default: null },
        navigationOptions: { type: Object, default: () => ({}) },
        navigationDisabled: { type: Boolean, default: false },
    },
    computed: {
        completeActionLabel() {
            const documentInformation = this.draft && this.draft.documentInformation

            return !this.navigation
                && documentInformation
                && documentInformation.relevance === 'irrelevant'
                ? this.translate(
                    'Component(ReviewProcess):confirm-as-not-relevant',
                    'Confirm as not relevant',
                )
                : this.translate(
                    'Component(ReviewProcess):complete',
                    'Complete review',
                )
        },
        completionAttentionChecks() {
            const checks = this.completionIssues
                .filter(issue => issue && issue.reason)
                .map((issue, index) => ({
                    ...issue,
                    id: issue.id || `completion-reason-${index}`,
                    status: 'needs_review',
                }))

            if (checks.length || this.completionAllowed) {
                return checks
            }

            return [{
                id: 'completion-blocked',
                status: 'needs_review',
                reason: {
                    messageKey: 'Component(ReviewProcess):complete-unavailable',
                    fallbackMessage: 'Resolve the remaining review issues before completing.',
                },
            }]
        },
        resolvedNavigationOptions() {
            return {
                showItemSelector: this.navigationOptions.showItemSelector !== false,
                showAdjacentNavigation:
                    this.navigationOptions.showAdjacentNavigation !== false,
                showProgress: this.navigationOptions.showProgress !== false,
                progressMode: this.navigationOptions.progressMode === 'position'
                    ? 'position'
                    : 'reviewed',
            }
        },
        navigationProgressLabel() {
            if (!this.navigation) {
                return ''
            }
            if (this.resolvedNavigationOptions.progressMode === 'position') {
                const current = this.navigation.complete
                    ? this.navigation.total
                    : this.navigation.current

                return this.translate(
                    'Component(ReviewProcess):document-position-count',
                    `${current} of ${this.navigation.total}`,
                    { current, total: this.navigation.total },
                )
            }

            return this.translate(
                'Component(ReviewProcess):documents-reviewed-count',
                `${this.navigation.reviewed} of ${this.navigation.total} reviewed`,
                { reviewed: this.navigation.reviewed, total: this.navigation.total },
            )
        },
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = this.$t(key, params)

            return translation && translation !== key ? translation : fallback
        },
    },
}
</script>

<style lang="scss">
.review-workspace-modal .modal-xl {
    max-width: calc(100vw - 3rem);
}

.review-workspace-footer,
.review-workspace-footer__navigation,
.review-workspace-footer__actions {
    display: flex;
    align-items: center;
    gap: .75rem;
}

.review-workspace-footer__complete-action {
    display: inline-flex;
    align-items: center;
    gap: .5rem;
}

.review-workspace-footer__complete-attention {
    display: inline-flex;
    flex: 0 0 auto;
}

.review-workspace-footer {
    justify-content: space-between;
    width: 100%;
    min-width: 0;
}

.review-workspace-footer__navigation {
    flex: 1 1 auto;
    min-width: 0;
}

.review-workspace-footer__select {
    width: auto;
    max-width: 24rem;
    min-width: 12rem;
}

.review-workspace-footer__progress {
    white-space: nowrap;
}

.review-workspace-footer__actions {
    flex: 0 0 auto;
    margin-left: auto;
}

.review-workspace-complete-state {
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

@media (max-width: 767.98px) {
    .review-workspace-footer {
        flex-wrap: wrap;
    }

    .review-workspace-footer__navigation,
    .review-workspace-footer__actions {
        flex-basis: 100%;
    }

    .review-workspace-footer__select {
        flex: 1 1 auto;
        max-width: none;
    }

    .review-workspace-footer__actions {
        justify-content: flex-end;
    }
}
</style>
